/**
 * Every card the Statistics page can show. Each administrator chooses which ones, where and how big (drag and drop on a
 * 12-column grid); the layout is saved on the server per administrator (GET/PUT /stats/layout).
 * A card added here appears automatically for everyone, at the end of their page.
 *
 * x / y / w / h: default position and size in grid cells (12 columns, ROW_HEIGHT px rows).
 */
export const COLUMNS = 12;
export const ROW_HEIGHT = 64;

export const CATEGORIES = ['Impact', 'Forests', 'Carbon credits', 'Activity'];

export const WIDGETS = [
  { id: 'impact', title: 'Climate impact', icon: 'fas fa-globe-europe', category: 'Impact', component: 'ImpactHero',
    description: 'The CO2 our forests hold, what it equals in everyday terms and what they capture while you watch.', x: 0, y: 0, w: 6, h: 5, minW: 4, minH: 4 },
  { id: 'kpi-co2', title: 'CO2 stored', icon: 'fas fa-cloud', category: 'Impact', component: 'KpiTile', props: { metric: 'co2' },
    description: 'Tonnes of CO2 stored in every forest at its latest scan.', x: 6, y: 0, w: 3, h: 2, minW: 2, minH: 2 },
  { id: 'kpi-yearly', title: 'CO2 per year', icon: 'fas fa-wind', category: 'Impact', component: 'KpiTile', props: { metric: 'yearly' },
    description: 'Tonnes of CO2 the forests capture each year (theoretical increment).', x: 9, y: 0, w: 3, h: 2, minW: 2, minH: 2 },
  { id: 'kpi-trees', title: 'Trees', icon: 'fas fa-seedling', category: 'Forests', component: 'KpiTile', props: { metric: 'trees' },
    description: 'Trees counted in every forest at its latest scan.', x: 6, y: 2, w: 3, h: 2, minW: 2, minH: 2 },
  { id: 'kpi-forests', title: 'Forests', icon: 'fas fa-tree', category: 'Forests', component: 'KpiTile', props: { metric: 'forests' },
    description: 'Forests on the platform and their hectares.', x: 9, y: 2, w: 3, h: 2, minW: 2, minH: 2 },
  { id: 'map', title: 'Forest map', icon: 'fas fa-map-marked-alt', category: 'Forests', component: 'ForestMap',
    description: 'Every forest on a 3D globe, sized by the CO2 it stores. Click one for its details.', x: 0, y: 5, w: 8, h: 7, minW: 4, minH: 5 },
  { id: 'species', title: 'Tree species', icon: 'fas fa-leaf', category: 'Forests', component: 'Species',
    description: 'Share of each species in the forests and the CO2 it accounts for.', x: 8, y: 5, w: 4, h: 7, minW: 3, minH: 5 },
  { id: 'co2-growth', title: 'CO2 growth', icon: 'fas fa-chart-area', category: 'Impact', component: 'Co2Growth',
    description: 'CO2 measured by the scans over time, accumulated, with the scans of each month.', x: 0, y: 12, w: 8, h: 5, minW: 4, minH: 4 },
  { id: 'token-flow', title: 'ZCO2 credits', icon: 'fas fa-coins', category: 'Carbon credits', component: 'TokenFlow',
    description: 'Where the ZCO2 credits are: available in the forests, retired, withdrawn and the on-chain buffer.', x: 8, y: 12, w: 4, h: 5, minW: 3, minH: 4 },
  { id: 'forecast', title: 'CO2 forecast', icon: 'fas fa-rocket', category: 'Impact', component: 'Forecast',
    description: 'How much CO2 the current forests will hold and capture every year until 2060.', x: 0, y: 17, w: 6, h: 5, minW: 4, minH: 4 },
  { id: 'growth', title: 'Forest growth', icon: 'fas fa-ruler-vertical', category: 'Forests', component: 'ForestGrowth',
    description: 'Average tree height and trunk diameter measured by the scans, year by year.', x: 6, y: 17, w: 6, h: 5, minW: 3, minH: 4 },
  { id: 'splits', title: 'Splits per month', icon: 'fas fa-hand-holding-heart', category: 'Carbon credits', component: 'SplitsDelivered',
    description: 'Tree splits delivered to partner companies every month, and who receives them.', x: 0, y: 22, w: 8, h: 5, minW: 4, minH: 4 },
  { id: 'tree-stock', title: 'Tree stock', icon: 'fas fa-boxes', category: 'Carbon credits', component: 'TreeStock',
    description: 'Trees free for splits, assigned to a company, already in use, and whole trees.', x: 8, y: 22, w: 4, h: 5, minW: 3, minH: 4 },
  { id: 'calendar', title: 'Scan calendar', icon: 'fas fa-calendar-alt', category: 'Activity', component: 'ScanCalendar',
    description: 'Forest scans day by day over the last twelve months.', x: 0, y: 27, w: 8, h: 4, minW: 6, minH: 3 },
  { id: 'live', title: 'Live scan activity', icon: 'fas fa-satellite-dish', category: 'Activity', component: 'LiveFeed',
    description: 'The latest forest scans, refreshed every 30 seconds.', x: 8, y: 27, w: 4, h: 4, minW: 3, minH: 3 },
  { id: 'top-forests', title: 'Top forests', icon: 'fas fa-trophy', category: 'Forests', component: 'TopForests',
    description: 'The forests that store the most CO2.', x: 0, y: 31, w: 6, h: 6, minW: 4, minH: 4 },
  { id: 'countries', title: 'Forests by country', icon: 'fas fa-flag', category: 'Forests', component: 'Countries',
    description: 'Forests, hectares, trees and CO2 per country.', x: 6, y: 31, w: 6, h: 6, minW: 3, minH: 4 },
  { id: 'retirements', title: 'Retirements', icon: 'fas fa-recycle', category: 'Carbon credits', component: 'Retirements',
    description: 'ZCO2 retired and withdrawn every month, and in total.', x: 0, y: 37, w: 6, h: 5, minW: 4, minH: 4 },
  { id: 'customers', title: 'Customer growth', icon: 'fas fa-users', category: 'Activity', component: 'Customers',
    description: 'Customers who joined every month, and the total.', x: 6, y: 37, w: 6, h: 5, minW: 3, minH: 4 },
  { id: 'kpi-available', title: 'ZCO2 available', icon: 'fas fa-gem', category: 'Carbon credits', component: 'KpiTile', props: { metric: 'available' },
    description: 'ZCO2 credits generated by the forests and not yet delivered.', x: 0, y: 42, w: 3, h: 2, minW: 2, minH: 2 },
  { id: 'kpi-retired', title: 'ZCO2 retired', icon: 'fas fa-fire-alt', category: 'Carbon credits', component: 'KpiTile', props: { metric: 'retired' },
    description: 'ZCO2 credits retired (offset for good).', x: 3, y: 42, w: 3, h: 2, minW: 2, minH: 2 },
  { id: 'kpi-customers', title: 'Customers', icon: 'fas fa-user-friends', category: 'Activity', component: 'KpiTile', props: { metric: 'customers' },
    description: 'Customers registered on the platform.', x: 6, y: 42, w: 3, h: 2, minW: 2, minH: 2 },
  { id: 'kpi-scans', title: 'Scans', icon: 'fas fa-satellite', category: 'Activity', component: 'KpiTile', props: { metric: 'scans' },
    description: 'Forest scans recorded, and when the last one was.', x: 9, y: 42, w: 3, h: 2, minW: 2, minH: 2 },
  { id: 'kpi-countries', title: 'Countries', icon: 'fas fa-globe-americas', category: 'Forests', component: 'KpiTile', props: { metric: 'countries' },
    description: 'Countries with forests on the platform.', x: 0, y: 44, w: 3, h: 2, minW: 2, minH: 2 },
  { id: 'kpi-species', title: 'Species', icon: 'fas fa-dna', category: 'Forests', component: 'KpiTile', props: { metric: 'species' },
    description: 'Different tree species in the forests.', x: 3, y: 44, w: 3, h: 2, minW: 2, minH: 2 },
  { id: 'kpi-splits', title: 'Splits delivered', icon: 'fas fa-box-open', category: 'Carbon credits', component: 'KpiTile', props: { metric: 'splits' },
    description: 'Tree splits delivered to partner companies.', x: 6, y: 44, w: 3, h: 2, minW: 2, minH: 2 },
];

export const byId = (id) => WIDGETS.find((w) => w.id === id) || null;

// cards about the whole platform's business: administrators only (a customer sees their own forests, nothing else)
const ADMIN_ONLY = ['splits', 'tree-stock', 'customers', 'kpi-customers', 'kpi-splits'];

/**
 * The boards built with these cards: which cards each one offers and where they start (cards of the catalog that are
 * not in `start` are offered hidden, in the "Cards" drawer).
 */
const at = (i, x, y, w, h) => ({ i, x, y, w, h });
export const BOARDS = {
  statistics: { scope: 'admin', start: WIDGETS.map((w) => at(w.id, w.x, w.y, w.w, w.h)) },
  'home-admin': {
    scope: 'admin',
    start: [
      at('impact', 0, 0, 6, 5), at('kpi-co2', 6, 0, 3, 2), at('kpi-yearly', 9, 0, 3, 2), at('kpi-available', 6, 2, 3, 2), at('kpi-splits', 9, 2, 3, 2),
      at('map', 0, 5, 8, 7), at('live', 8, 5, 4, 7),
      at('co2-growth', 0, 12, 8, 5), at('token-flow', 8, 12, 4, 5),
      at('splits', 0, 17, 8, 5), at('tree-stock', 8, 17, 4, 5),
      at('forecast', 0, 22, 6, 5), at('top-forests', 6, 22, 6, 5),
    ],
  },
  'home-user': {
    scope: 'user',
    start: [
      at('impact', 0, 0, 6, 5), at('kpi-co2', 6, 0, 3, 2), at('kpi-yearly', 9, 0, 3, 2), at('kpi-forests', 6, 2, 3, 2), at('kpi-available', 9, 2, 3, 2),
      at('map', 0, 5, 8, 7), at('species', 8, 5, 4, 7),
      at('co2-growth', 0, 12, 8, 5), at('token-flow', 8, 12, 4, 5),
      at('forecast', 0, 17, 6, 5), at('growth', 6, 17, 6, 5),
      at('top-forests', 0, 22, 6, 6), at('live', 6, 22, 6, 6),
      at('calendar', 0, 28, 8, 4), at('kpi-retired', 8, 28, 4, 2), at('kpi-scans', 8, 30, 4, 2),
      at('retirements', 0, 32, 12, 5),
    ],
  },
};

/** The cards a board offers. */
export function widgetsFor(board) {
  const b = BOARDS[board] || BOARDS.statistics;
  return WIDGETS.filter((w) => b.scope === 'admin' || !ADMIN_ONLY.includes(w.id));
}

/** The layout of somebody who never customised the board: its starting cards, the rest of its catalog hidden. */
export function defaultLayout(board = 'statistics') {
  const b = BOARDS[board] || BOARDS.statistics;
  const allowed = new Set(widgetsFor(board).map((w) => w.id));
  const out = b.start.filter((l) => allowed.has(l.i)).map((l) => ({ ...l, visible: true }));
  const bottom = out.reduce((m, l) => Math.max(m, l.y + l.h), 0);
  for (const w of widgetsFor(board)) if (!out.some((l) => l.i === w.id)) out.push({ i: w.id, x: 0, y: bottom, w: w.w, h: w.h, visible: false });
  return out;
}

/**
 * A saved layout made usable: unknown cards (or cards this board does not offer) dropped, sizes kept within each card's
 * limits, and cards added to the catalog since it was saved appended at the bottom.
 */
export function mergeLayout(saved, board = 'statistics') {
  if (!saved || !Array.isArray(saved.widgets) || !saved.widgets.length) return defaultLayout(board);
  const allowed = new Set(widgetsFor(board).map((w) => w.id));
  const out = [];
  const seen = new Set();
  let bottom = 0;
  for (const s of saved.widgets) {
    const def = byId(s.id);
    if (!def || seen.has(s.id) || !allowed.has(s.id)) continue;
    seen.add(s.id);
    const w = Math.max(def.minW || 1, Math.min(COLUMNS, Number(s.w) || def.w));
    const h = Math.max(def.minH || 1, Math.min(40, Number(s.h) || def.h));
    const x = Math.max(0, Math.min(COLUMNS - w, Number(s.x) || 0));
    const y = Math.max(0, Number(s.y) || 0);
    out.push({ i: s.id, x, y, w, h, visible: s.visible !== false });
    if (s.visible !== false) bottom = Math.max(bottom, y + h);
  }
  // new cards fill rows at the bottom, side by side
  let x = 0;
  let rowHeight = 0;
  for (const def of widgetsFor(board)) {
    if (seen.has(def.id)) continue;
    if (x + def.w > COLUMNS) { bottom += rowHeight; x = 0; rowHeight = 0; }
    out.push({ i: def.id, x, y: bottom, w: def.w, h: def.h, visible: true });
    x += def.w;
    rowHeight = Math.max(rowHeight, def.h);
  }
  return out;
}

/** What is sent to the server. */
export const toSaved = (layout) => ({ widgets: layout.map((l) => ({ id: l.i, x: l.x, y: l.y, w: l.w, h: l.h, visible: l.visible !== false })) });
